// frontend/source/portal/components/ImagePicker.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import adminApi from "../../services/adminapi";
import ConfirmDialog from "./ConfirmDialog";
import "./ImagePicker.css";

const ITEMS_PER_PAGE = 10;

const ImagePicker = ({
  currentImage,
  onImageSelect,
  onClose,
  imageType = "works", //students, lig
}) => {
  const [activeTab, setActiveTab] = useState("upload"); // Upload tab is default
  const [filterTab, setFilterTab] = useState("artworks"); // Default to artworks (most numerous)
  const [displayedImages, setDisplayedImages] = useState([]); // Images currently displayed
  const [selectedImage, setSelectedImage] = useState(currentImage);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedForDeletion, setSelectedForDeletion] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasLoadedImages, setHasLoadedImages] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterCounts, setFilterCounts] = useState({
    categories: 0,
    tags: 0,
    artworks: 0,
    unused: 0,
  });

  const galleryRef = useRef(null);
  const observerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);

  // Load filter counts when switching to existing tab for the first time
  useEffect(() => {
    if (activeTab === "existing" && !hasLoadedImages) {
      loadFilterCounts();
      loadImages(1); // Load first page
    }
  }, [activeTab]);

  // Reset and reload when filter or search changes
  useEffect(() => {
    if (hasLoadedImages && activeTab === "existing") {
      setCurrentPage(1);
      setDisplayedImages([]);
      setHasMore(true);
      loadImages(1, true); // true = reset
    }
  }, [filterTab, searchTerm]);

  const loadFilterCounts = async () => {
    try {
      const counts = await adminApi.getImageCounts();
      setFilterCounts(counts);
    } catch (err) {
      console.error("Failed to load filter counts:", err);
    }
  };

  const loadImages = async (page = 1, reset = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await adminApi.getImages({
        page,
        limit: ITEMS_PER_PAGE,
        filter: filterTab,
        search: searchTerm,
      });

      setHasLoadedImages(true);

      if (reset || page === 1) {
        setDisplayedImages(response.images);
      } else {
        setDisplayedImages((prev) => [...prev, ...response.images]);
      }

      setCurrentPage(page);
      setHasMore(response.pagination.hasMore);
      setError(null);
    } catch (err) {
      setError("Failed to load images");
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreImages = useCallback(() => {
    if (loadingMore || !hasMore || loading) return;
    loadImages(currentPage + 1);
  }, [currentPage, loadingMore, hasMore, loading]);

  // Set up Intersection Observer for infinite scroll
  useEffect(() => {
    if (activeTab !== "existing" || !hasLoadedImages) return;

    const options = {
      root: galleryRef.current,
      rootMargin: "200px",
      threshold: 0.1,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !loadingMore && !loading) {
        loadMoreImages();
      }
    }, options);

    if (loadMoreTriggerRef.current) {
      observerRef.current.observe(loadMoreTriggerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    activeTab,
    hasLoadedImages,
    hasMore,
    loadingMore,
    loading,
    loadMoreImages,
  ]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }
      setUploadFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleUploadAndSelect = async () => {
    if (!uploadFile) return;

    try {
      setLoading(true);
      const uploadedImage = await adminApi.uploadImage(uploadFile, imageType);
      setSelectedImage(uploadedImage.url);
      setActiveTab("existing");
      setUploadFile(null);
      setUploadPreview(null);
      setError(null);

      // Refresh the current view to include the new image
      if (hasLoadedImages) {
        // Reload filter counts
        loadFilterCounts();
        // Reload current filter
        loadImages(1, true);
      }
    } catch (err) {
      let errMsg = err.userMessage || err.message;
      setError(`Failed to upload image. ${errMsg}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (selectedImage) {
      onImageSelect(selectedImage);
      onClose();
    }
  };

  // Reset search when changing filter tabs
  const handleFilterChange = (newFilter) => {
    setFilterTab(newFilter);
    setSearchTerm("");
    setSelectedForDeletion(new Set()); // Clear deletion selection
  };

  // Check if search is available for current filter
  const isSearchAvailable = ["categories", "tags", "artworks"].includes(
    filterTab,
  );

  // Toggle image selection for deletion
  const toggleImageForDeletion = (imageUrl, event) => {
    event.stopPropagation(); // Prevent selecting for picker
    const newSelection = new Set(selectedForDeletion);
    if (newSelection.has(imageUrl)) {
      newSelection.delete(imageUrl);
    } else {
      newSelection.add(imageUrl);
    }
    setSelectedForDeletion(newSelection);
  };

  // Select all visible unused images (current page only)
  const selectAllUnused = () => {
    const visibleUnusedUrls = displayedImages.map((img) => img.url);
    setSelectedForDeletion(new Set(visibleUnusedUrls));
  };

  // Clear deletion selection
  const clearDeletionSelection = () => {
    setSelectedForDeletion(new Set());
  };

  // Confirm deletion
  const handleDeleteConfirm = () => {
    if (selectedForDeletion.size === 0) return;
    setShowDeleteConfirm(true);
  };

  // Execute deletion
  const executeDelete = async () => {
    try {
      setIsDeleting(true);
      const urlsToDelete = Array.from(selectedForDeletion);

      // Delete all images in a single batch request
      await adminApi.deleteImages(urlsToDelete);

      // Remove deleted images from displayed list
      setDisplayedImages(
        displayedImages.filter((img) => !selectedForDeletion.has(img.url)),
      );

      // Clear selection and refresh counts
      setSelectedForDeletion(new Set());
      setShowDeleteConfirm(false);
      setError(null);

      // Reload filter counts
      loadFilterCounts();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to delete images. Please try again.",
      );
      console.error(err);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // Get association badges for an image
  const getAssociationBadges = (img) => {
    const badges = [];
    if (img.usedInCategories && img.usedInCategories.length > 0) {
      badges.push({ type: "category", count: img.usedInCategories.length });
    }
    if (img.usedInArtTags && img.usedInArtTags.length > 0) {
      badges.push({ type: "tag", count: img.usedInArtTags.length });
    }
    if (img.usedInArtworks && img.usedInArtworks.length > 0) {
      badges.push({ type: "artwork", count: img.usedInArtworks.length });
    }
    return badges;
  };

  const modalContent = (
    <div className="image-picker-overlay" onClick={onClose}>
      <div className="image-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="image-picker-header">
          <h2 className="image-picker-title">Select Category Image</h2>
          <button className="image-picker-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="image-picker-tabs">
          <button
            className={`image-picker-tab ${
              activeTab === "upload" ? "active" : ""
            }`}
            onClick={() => setActiveTab("upload")}
          >
            Upload New
          </button>
          <button
            className={`image-picker-tab ${
              activeTab === "existing" ? "active" : ""
            }`}
            onClick={() => setActiveTab("existing")}
          >
            Existing Images
          </button>
        </div>

        {activeTab === "existing" && (
          <div className="image-picker-filter-tabs">
            <button
              className={`image-picker-filter-tab ${
                filterTab === "artworks" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("artworks")}
            >
              Artworks ({filterCounts.artworks})
            </button>
            <button
              className={`image-picker-filter-tab ${
                filterTab === "categories" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("categories")}
            >
              Categories ({filterCounts.categories})
            </button>
            <button
              className={`image-picker-filter-tab ${
                filterTab === "tags" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("tags")}
            >
              Art Tags ({filterCounts.tags})
            </button>
            <button
              className={`image-picker-filter-tab ${
                filterTab === "unused" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("unused")}
            >
              Unused ({filterCounts.unused})
            </button>
          </div>
        )}

        {activeTab === "existing" && isSearchAvailable && (
          <div className="image-picker-search-bar">
            <input
              type="text"
              placeholder={`Search by ${
                filterTab === "categories"
                  ? "category"
                  : filterTab === "tags"
                    ? "art tag"
                    : "artwork"
              } name...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="image-picker-search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="image-picker-search-clear"
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
        )}

        {activeTab === "existing" && filterTab === "unused" && (
          <div className="image-picker-delete-bar">
            <div className="image-picker-delete-info">
              {selectedForDeletion.size > 0 ? (
                <>
                  <span className="delete-count">
                    {selectedForDeletion.size} selected
                  </span>
                  <button
                    onClick={clearDeletionSelection}
                    className="image-picker-delete-action-btn clear-btn"
                  >
                    Clear Selection
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className="image-picker-delete-action-btn delete-btn"
                  >
                    Delete Selected
                  </button>
                </>
              ) : (
                <>
                  <span className="delete-hint">
                    Click images to select for deletion
                  </span>
                  <button
                    onClick={selectAllUnused}
                    className="image-picker-delete-action-btn select-all-btn"
                    disabled={filterCounts.unused === 0}
                  >
                    Select All Visible
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {error && <div className="image-picker-error">{error}</div>}

        <div className="image-picker-content" ref={galleryRef}>
          {activeTab === "upload" && (
            <div className="image-picker-upload">
              <div className="image-picker-upload-area">
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="image-picker-file-input"
                />
                <label
                  htmlFor="image-upload"
                  className="image-picker-upload-label"
                >
                  {uploadPreview ? (
                    <img
                      src={uploadPreview}
                      alt="Upload preview"
                      className="image-picker-preview"
                    />
                  ) : (
                    <div className="image-picker-upload-placeholder">
                      <div className="image-picker-upload-icon">📁</div>
                      <p>Click to select an image</p>
                      <p className="image-picker-upload-hint">
                        JPG, PNG, GIF up to 10MB
                      </p>
                    </div>
                  )}
                </label>
              </div>

              {uploadFile && (
                <div className="image-picker-upload-actions">
                  <button
                    onClick={handleUploadAndSelect}
                    className="image-picker-btn upload-btn"
                    disabled={loading}
                  >
                    {loading ? "Uploading..." : "Upload & Select"}
                  </button>
                  <button
                    onClick={() => {
                      setUploadFile(null);
                      setUploadPreview(null);
                    }}
                    className="image-picker-btn cancel-upload-btn"
                    disabled={loading}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === "existing" && (
            <div className="image-picker-gallery">
              {loading && currentPage === 1 ? (
                <div className="image-picker-loading">Loading images...</div>
              ) : displayedImages.length === 0 ? (
                <div className="image-picker-empty">
                  {searchTerm
                    ? `No images found matching "${searchTerm}"`
                    : `No images in ${filterTab}`}
                </div>
              ) : (
                <>
                  {displayedImages.map((img) => {
                    const badges = getAssociationBadges(img);
                    const isUnusedFilter = filterTab === "unused";
                    const isSelectedForDel = selectedForDeletion.has(img.url);
                    const isSelectedForPicker = selectedImage === img.url;

                    return (
                      <div
                        key={img.filename}
                        className={`image-picker-item ${
                          isSelectedForPicker ? "selected" : ""
                        } ${isSelectedForDel ? "marked-for-deletion" : ""}`}
                        onClick={(e) => {
                          if (isUnusedFilter) {
                            toggleImageForDeletion(img.url, e);
                          } else {
                            // Toggle selection - deselect if already selected
                            if (isSelectedForPicker) {
                              setSelectedImage(null);
                            } else {
                              setSelectedImage(img.url);
                            }
                          }
                        }}
                      >
                        <img
                          src={img.url}
                          alt={img.filename}
                          className="image-picker-thumbnail"
                        />
                        {badges.length > 0 && (
                          <div className="image-picker-badges">
                            {badges.map((badge, idx) => (
                              <span
                                key={idx}
                                className={`image-picker-badge ${badge.type}`}
                                title={`Used in ${badge.count} ${badge.type}${
                                  badge.count > 1 ? "s" : ""
                                }`}
                              >
                                {badge.type === "category" && "📂"}
                                {badge.type === "tag" && "🏷️"}
                                {badge.type === "artwork" && "🖼️"}
                                {badge.count > 1 && (
                                  <span className="badge-count">
                                    {badge.count}
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                        {isUnusedFilter && isSelectedForDel && (
                          <div className="image-picker-delete-check">🗑️</div>
                        )}
                        {!isUnusedFilter && isSelectedForPicker && (
                          <div className="image-picker-check">✓</div>
                        )}
                      </div>
                    );
                  })}
                  {hasMore && (
                    <div
                      ref={loadMoreTriggerRef}
                      className="image-picker-load-more-trigger"
                    >
                      {loadingMore && (
                        <div className="image-picker-loading-more">
                          Loading more...
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="image-picker-footer">
          <button onClick={onClose} className="image-picker-btn secondary-btn">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="image-picker-btn primary-btn"
            disabled={!selectedImage || selectedImage === currentImage}
          >
            Save Selection
          </button>
        </div>

        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Delete Images?"
          message={`Are you sure you want to permanently delete ${
            selectedForDeletion.size
          } image${
            selectedForDeletion.size > 1 ? "s" : ""
          }? This action cannot be undone.`}
          confirmText={isDeleting ? "Deleting..." : "Delete Permanently"}
          cancelText="Cancel"
          type="danger"
          onConfirm={executeDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ImagePicker;
